import {
  BANNER_VIEW_STORAGE_KEY,
  BANNER_VIEW_THRESHOLD,
} from '@/constants/banner';
import { createBannerActivity } from '@/lib/api/common/banner.api';
import type { BannerViewCache } from '@/types/banner';

export function getLocalDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function readBannerViewCache(): BannerViewCache {
  if (typeof window === 'undefined') {
    return { date: getLocalDateKey(), ids: [] };
  }

  try {
    const raw = window.localStorage.getItem(BANNER_VIEW_STORAGE_KEY);
    if (!raw) {
      return { date: getLocalDateKey(), ids: [] };
    }

    const parsed = JSON.parse(raw) as BannerViewCache;
    if (
      typeof parsed?.date !== 'string' ||
      !Array.isArray(parsed.ids)
    ) {
      return { date: getLocalDateKey(), ids: [] };
    }

    return {
      date: parsed.date,
      ids: parsed.ids.filter((id): id is number => Number.isInteger(id)),
    };
  } catch {
    return { date: getLocalDateKey(), ids: [] };
  }
}

function writeBannerViewCache(cache: BannerViewCache): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    BANNER_VIEW_STORAGE_KEY,
    JSON.stringify(cache)
  );
}

export function hasBannerViewedToday(id: number): boolean {
  const today = getLocalDateKey();
  const cache = readBannerViewCache();
  return cache.date === today && cache.ids.includes(id);
}

export function claimBannerView(id: number): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const today = getLocalDateKey();
  const cache = readBannerViewCache();
  const ids = cache.date === today ? cache.ids : [];

  if (ids.includes(id)) {
    return false;
  }

  writeBannerViewCache({ date: today, ids: [...ids, id] });
  return true;
}

export function getVisibleRatio(element: Element): number {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return 0;
  }

  const visibleWidth =
    Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
  const visibleHeight =
    Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

  if (visibleWidth <= 0 || visibleHeight <= 0) {
    return 0;
  }

  return (visibleWidth * visibleHeight) / (rect.width * rect.height);
}

export function isBannerMostlyVisible(element: Element): boolean {
  return getVisibleRatio(element) >= BANNER_VIEW_THRESHOLD;
}

export function trackBannerImpression(id: number): void {
  if (!claimBannerView(id)) {
    return;
  }

  void createBannerActivity({ bannerId: id, type: 'impression' }).catch(
    () => undefined
  );
}

export function shuffleBanners<T>(items: T[]): T[] {
  const copy = [...items];

  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
}
